import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { MdPlayCircle } from 'react-icons/md';
import { SanityImage } from 'sanity-image';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '../shadcn/Carousel';

import { Dialog, DialogContent, DialogTrigger } from '../shadcn/Dialog';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import {
  FeaturedUGCVideosProps,
  FeaturedUGCVideo,
} from './FeaturedUGCVideos.types';

const sanityBaseUrl = `https://cdn.sanity.io/images/${process.env.GATSBY_SANITY_PROJECT_ID}/${process.env.GATSBY_SANITY_DATASET}/`;

function FeaturedUGCVideos({
  value,
}: Readonly<FeaturedUGCVideosProps>): React.ReactElement {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState<number>(0);
  const [activeItem, setActiveItem] = useState<FeaturedUGCVideo>();

  const { tt } = useUiComponentsInternationalContext();

  let videoUrl: string;

  switch (activeItem?.videoType) {
    case 'vimeo':
      videoUrl = activeItem?.vimeoVideo || '';
      break;

    case 'youtube':
      videoUrl = activeItem?.youtubeVideo || '';
      break;

    case 'sanity':
      videoUrl = activeItem?.sanityVideo?.asset?.url || '';
      break;

    default:
      videoUrl = '';
  }

  const handleItemClick = (item: FeaturedUGCVideo) => {
    setActiveItem(item);
  };

  const renderItem = (item: FeaturedUGCVideo) => (
    <DialogTrigger
      asChild
      onClick={() => {
        handleItemClick(item);
      }}
    >
      <button
        type="button"
        className="flex flex-col h-full w-full p-0 bg-background border border-solid cursor-pointer hover:border-primary rounded overflow-hidden"
        aria-label={tt(s => s.featuredUgcVideosComponent.openVideoAriaLabel, {
          headline: item?.headline || '',
        })}
      >
        {item?.thumbnail?.asset?._id && (
          <div className="flex-grow-0 w-full relative">
            <SanityImage
              id={item.thumbnail.asset._id}
              baseUrl={sanityBaseUrl}
              className="block w-full h-auto aspect-[11/16]  object-cover self-start"
              alt={
                item.alt ||
                item.headline ||
                tt(s => s.featuredUgcVideosComponent.videoThumbnailAlt)
              }
              loading="lazy"
              hotspot={item.thumbnail.asset?.hotspot}
              crop={item.thumbnail.asset?.crop}
            />
            <div className="absolute inset-0 flex items-center justify-center text-primary">
              <MdPlayCircle fill="white" size="36" />
            </div>
          </div>
        )}

        <div className="p-4 text-left">
          <div className="mb-1 font-bold text-primary">
            {item?.headline || ''}
          </div>
          <div>{item?.copy || ''}</div>
        </div>
      </button>
    </DialogTrigger>
  );

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  return (
    <div className="py-5">
      {value?.headline && <h2 className="mb-1">{value.headline}</h2>}

      <Dialog>
        {/* Mobile Carousel */}
        <div className="md:hidden">
          <Carousel
            setApi={setApi}
            opts={{
              align: 'start',
              loop: true,
            }}
          >
            <CarouselContent>
              {value?.items?.map(item => (
                <CarouselItem key={item.copy}>{renderItem(item)}</CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <div className="flex justify-center mt-2">
            {value?.items?.map((item, index) => (
              <button
                type="button"
                aria-label={tt(
                  s => s.featuredUgcVideosComponent.openVideoAriaLabel,
                  {
                    headline: item?.headline || '',
                  }
                )}
                key={`bullet-${item._key}`}
                className={`w-4 h-4 rounded-full mx-1 ${
                  index + 1 === current ? 'bg-primary' : 'bg-muted'
                }`}
                onClick={() => {
                  api.scrollTo(index);
                }}
              />
            ))}
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6 mt-10">
          {value?.items?.map(item => (
            <div key={item.copy}>{renderItem(item)}</div>
          ))}
        </div>

        {/* Modal/Dialog content */}
        <DialogContent className="sm:rounded-sm h-4/5 max-h-[70vh]">
          {videoUrl && (
            <div className="relative  mt-2 h-full">
              <ReactPlayer
                url={videoUrl}
                controls
                playsinline
                muted
                playing
                className="absolute top-0 left-0"
                width="100%"
                height="100%"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FeaturedUGCVideos;
